"use client";

import { useEffect, useRef } from "react";

export default function ParticlesBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    function resizeCanvas() { 
      if(canvas) {
        canvas.width = window.innerWidth; 
        canvas.height = window.innerHeight; 
      }
    }
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const nodes: Node[] = [];
    const maxNodes = 60;

    class Node {
      x: number = 0;
      y: number = 0;
      vx: number = 0;
      vy: number = 0;
      size: number = 0;

      constructor() { 
        this.reset(); 
        if(canvas) this.y = Math.random() * canvas.height; 
      }
      
      reset() {
        if(!canvas) return;
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.4;
        this.vy = (Math.random() - 0.5) * 0.4;
        this.size = Math.random() * 4 + 2; 
      }
      
      update() {
        if(!canvas) return;
        this.x += this.vx; this.y += this.vy;
        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;
      }
      
      draw() {
        if(!ctx) return;
        ctx.fillStyle = document.documentElement.classList.contains('dark') ? 'rgba(139, 92, 246, 0.4)' : 'rgba(92, 23, 235, 0.2)';
        ctx.beginPath(); 
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2); 
        ctx.fill();
      }
    }

    for (let i = 0; i < maxNodes; i++) nodes.push(new Node());

    let animationFrameId: number;
    let animationRunning = true;

    function animate() {
      if(!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const isDark = document.documentElement.classList.contains('dark');

      for (let i = 0; i < nodes.length; i++) {
        nodes[i].update();
        nodes[i].draw();
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            ctx.strokeStyle = isDark ? 'rgba(139, 92, 246, ' + ((1 - dist / 130) * 0.15) + ')' : 'rgba(139, 92, 246, ' + ((1 - dist / 130) * 0.08) + ')';
            ctx.lineWidth = 0.5;
            ctx.beginPath(); ctx.moveTo(nodes[i].x, nodes[i].y); ctx.lineTo(nodes[j].x, nodes[j].y); ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(animate);
    }
    
    animate();

    const handleVisibility = () => {
      if (document.hidden) {
        animationRunning = false;
        cancelAnimationFrame(animationFrameId);
      } else {
        if (!animationRunning) {
          animationRunning = true;
          animate();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      document.removeEventListener('visibilitychange', handleVisibility);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <canvas id="particles-canvas" ref={canvasRef}></canvas>;
}
